import { Separator } from '@/components/ui/separator'
import ProTip from '../ProTip'

export default function CommonTasksSection() {
  return (
    <>
      <p className="text-sm text-muted-foreground mb-3">
        These step-by-step guides cover the actions you will use most often. Bookmark this section
        and come back whenever you need a refresher.
      </p>

      <section aria-labelledby="creating-records-heading">
        <h3 id="creating-records-heading" className="font-semibold mb-2">
          Creating New Records
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          Whether it is a new lead from a trade show or an account from a referral, the process is
          the same:
        </p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>Navigate to the module you need (e.g., CRM &rarr; Leads, CRM &rarr; Accounts)</li>
          <li>
            Click the <span className="font-medium">New</span> button in the top-right area
          </li>
          <li>Fill in the required fields (marked with an asterisk *)</li>
          <li>
            Add any optional details — the more context you capture now, the less you need to
            remember later
          </li>
          <li>
            Click <span className="font-medium">Save</span>
          </li>
        </ol>
        <p className="text-sm text-muted-foreground mt-2">
          The system checks for duplicates as you type, so if a similar record already exists, you
          will see a warning before saving.
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          <span className="font-medium">Time-savers:</span> When creating Opportunities or Leads,
          the Owner field defaults to you. For address fields, start typing and select from Google
          Maps suggestions — the city, state, postal code, and country fill in automatically.
        </p>
      </section>
      <Separator />

      <section aria-labelledby="editing-records-heading">
        <h3 id="editing-records-heading" className="font-semibold mb-2">
          Editing Records
        </h3>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>Click on a record name to open its detail page</li>
          <li>
            Click the <span className="font-medium">Edit</span> button (or click directly on a field
            to edit it inline)
          </li>
          <li>Make your changes</li>
          <li>
            Click <span className="font-medium">Save</span> to apply
          </li>
        </ol>
        <p className="text-sm text-muted-foreground mt-2">
          All changes are recorded in the History tab so you can see what was updated and when.
        </p>
      </section>
      <Separator />

      <section aria-labelledby="filtering-heading">
        <h3 id="filtering-heading" className="font-semibold mb-2">
          Filtering and Searching
        </h3>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            Use the <span className="font-medium">search box</span> at the top of any list page to
            find records by name, email, or other key fields
          </li>
          <li>
            The <span className="font-medium">global search bar</span> shows your recent searches in
            a dropdown — click to re-run a previous search without retyping
          </li>
          <li>
            Use <span className="font-medium">filter dropdowns</span> to narrow results by status,
            owner, date range, or other criteria
          </li>
          <li>
            Toggle <span className="font-medium">My / All</span> to switch between your records and
            the full list
          </li>
          <li>Click column headers to sort — click again to reverse the order</li>
          <li>Resize columns by dragging the column borders (your sizes are remembered)</li>
        </ul>
      </section>
      <Separator />

      <section aria-labelledby="bulk-actions-heading">
        <h3 id="bulk-actions-heading" className="font-semibold mb-2">
          Bulk Actions
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          When you need to update, reassign, or delete multiple records at once:
        </p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>Select records using the checkboxes on the left side of the list</li>
          <li>A bulk actions bar appears at the bottom showing how many records are selected</li>
          <li>
            Choose an action: <span className="font-medium">Delete</span>,{' '}
            <span className="font-medium">Update</span> (change status, stage, etc.), or{' '}
            <span className="font-medium">Assign</span> (reassign to a different owner)
          </li>
          <li>Confirm the operation</li>
        </ol>
        <ProTip>
          <span className="font-medium">Clean up fast:</span> After a trade show, use bulk actions
          to assign all the new leads to the right reps in one go instead of editing them one by
          one.
        </ProTip>
      </section>
    </>
  )
}
